<ion-header>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button icon="chevron-back"></ion-back-button>
    </ion-buttons>

    <ion-title>Create a Last Minute Job</ion-title>
  </ion-toolbar>
</ion-header>

<ion-content>
  <app-prefill-lmj-info-from-session-banner
    *ngIf="prefillLmjInfoFromSessionContext.hasBeenLoadedAtLeastOnce"
    [bannerStatus]="prefillLmjInfoFromSessionContext.bannerStatus"
    (retryPrefill)="closePrefillLMJBanner(); prefillLmjInfoFromSession();"
    (dismissBanner)="closePrefillLMJBanner();"
  >
  </app-prefill-lmj-info-from-session-banner>

  <mable-banner
    category="headed-callout"
    heading="Tip"
    icon="information-circle"
    headingType="h2"
  >
    <ng-container class="content">
      <p>
        To get the best match, include all relevant information about the job. Workers can’t read your profile until you
        offer them the job.
      </p>
    </ng-container>
  </mable-banner>

  <form
    [formGroup]="form"
    [class.submitted]="submitAttempt"
  >
    <mable-input
      label="Job title"
      [hasError]="form.get('title').showError()"
    >
      <ion-textarea
        autocapitalize="sentences"
        autosize
        rows="1"
        name="title"
        placeholder="E.g. personal carer for an adult"
        formControlName="title"
        autocomplete="on"
        autocorrect="on"
        spellcheck="true"
        [attr.aria-label]="'Please enter the job title'"
      >
      </ion-textarea>
      <div class="warn">
        <p *ngIf="form.get('title').errors?.required">Please enter a title.</p>
        <p *ngIf="form.get('title').errors?.minlength">Please enter a longer title.</p>
        <p
          *ngIf="form?.controls?.title?.errors?.errorMessage && !form?.controls?.title?.errors?.required && !form.controls.title.errors?.minlength"
        >
          {{form?.controls?.title?.errors?.errorMessage}}
        </p>
      </div>
    </mable-input>

    <mable-input
      label="What suburb is the job in?"
      [hasError]="submitAttempt && !validPostCode()"
    >
      <app-suburb-selector-modal-input
        [isRequired]="submitAttempt && !validPostCode()"
        displayField="address"
        [suburb]="getSuburbName()"
        (updated)="suburbChanged($event)"
      >
      </app-suburb-selector-modal-input>
      <p
        class="warn"
        *ngIf="submitAttempt && !validPostCode()"
      >
        Please select suburb from dropdown.
      </p>
    </mable-input>

    <mable-input
      label="When do you require support?"
      [noBorder]="true"
      [hasError]="!form.controls.start_date.valid && submitAttempt"
    >
      <mb-radio-group
        formControlName="start_date"
        [options]="dayOptions"
        size="medium"
        (ionChange)="onChangeRequireSupport()"
      >
      </mb-radio-group>
      <div class="warn">
        <p>Please enter the day when you require support</p>
      </div>
    </mable-input>

    <div class="width60">
      <mable-input
        label="Start time"
        [hasError]="form.get('start_time').showError()"
      >
        <mable-time
          [minuteValues]="minutes"
          formControlName="start_time"
        ></mable-time>
        <div class="warn">
          <p *ngIf="form?.controls?.start_time?.errors?.required">Please select a start time.</p>
          <p *ngIf="form?.controls?.start_time?.errors?.errorMessage && !form?.controls?.start_time?.errors?.required">
            {{form?.controls?.start_time?.errors?.errorMessage}}
          </p>
        </div>
      </mable-input>
    </div>

    <div class="width60">
      <mable-input
        label="Duration (in hours)"
        [hasError]="form.get('duration').showError()"
      >
        <app-numeric-input
          formControlName="duration"
          aria-required="true"
          [required]="true"
        >
        </app-numeric-input>
        <div class="warn">
          <p *ngIf="form.controls.duration.errors?.required">Please enter a duration.</p>
          <p *ngIf="form.controls.duration.errors?.min || form.controls.duration.errors?.max">
            Please enter a duration between 1 to 24.
          </p>
        </div>
      </mable-input>
    </div>

    <mable-input
      label="Describe the support required"
      [hasError]="form.get('job_description').showError()"
    >
      <div class="description">
        <p>
          Describe key <span class="text-bold">activities, needs</span> and
          <span class="text-bold">worker requirements</span> in detail.
        </p>
      </div>
      <ion-textarea
        autocapitalize="sentences"
        autosize
        rows="7"
        name="description"
        placeholder="I need..."
        formControlName="job_description"
        autocomplete="on"
        autocorrect="on"
        spellcheck="true"
        [attr.aria-label]="'Please describe key activities needs and worker requirements'"
      >
      </ion-textarea>
      <div class="warn">
        <p *ngIf="form.get('job_description').errors?.required">Please enter a description.</p>
        <p *ngIf="form.get('job_description').errors?.minlength">Please enter a longer description.</p>
        <p
          *ngIf="form?.controls?.job_description?.errors?.errorMessage && !form?.controls?.job_description?.errors?.required && !form?.controls?.job_description?.errors?.minlength"
        >
          {{form?.controls?.job_description?.errors?.errorMessage}}
        </p>
      </div>
    </mable-input>

    <mable-input
      label="What is important for support people to know about the person seeking support?"
      [hasError]="form.get('consumer_description').showError()"
    >
      <div class="description">
        <p>May include <span class="text-bold">age, interests, medical information</span>, etc.</p>
      </div>
      <ion-textarea
        autocapitalize="sentences"
        autosize
        rows="7"
        name="consumer_description"
        placeholder="I am..."
        formControlName="consumer_description"
        autocomplete="on"
        autocorrect="on"
        spellcheck="true"
        [attr.aria-label]="'Please enter the important information about the person seeking support'"
      >
      </ion-textarea>
      <div class="warn">
        <p *ngIf="form?.controls?.consumer_description?.errors?.errorMessage">
          {{form?.controls?.consumer_description?.errors?.errorMessage}}
        </p>
      </div>
    </mable-input>

    <mable-input label="Gender preference (optional)">
      <ion-select
        aria-label="Gender preference (optional)"
        size="12"
        [(ngModel)]="job.gender"
        formControlName="gender"
        placeholder="Select an option"
      >
        <ion-select-option value="Female">Female</ion-select-option>
        <ion-select-option value="Male">Male</ion-select-option>
        <ion-select-option value="">No preference</ion-select-option>
      </ion-select>
    </mable-input>

    <mable-input
      label="Qualified service needs (optional)"
      [noBorder]="true"
    >
      <div class="description">
        <p>
          Workers who don’t have all the required qualifications will not be notified. Please be accurate with what you
          need.
        </p>
      </div>
      <app-worker-services
        [isFlex]="true"
        [selectedServices]="form.value['search_filter']['basic_search']"
        (selectedServicesChange)="updateServices($event)"
      >
      </app-worker-services>
    </mable-input>

    <ion-grid>
      <ion-row>
        <ion-col size="6">
          <mable-button
            expand="block"
            category="outline"
            (onClick)="closePage()"
            >Cancel</mable-button
          >
        </ion-col>
        <ion-col size="6">
          <mable-button
            expand="block"
            (onClick)="process()"
            >Post Job</mable-button
          >
        </ion-col>
      </ion-row>
    </ion-grid>
  </form>
</ion-content>
